import React, { useEffect } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { IconButton, Stack, Typography } from '@mui/material'
import { useFieldArray, useWatch } from 'react-hook-form'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { getEvseEfficientPvScheduleLocalStorageKey, getEvseSafePvScheduleLocalStorageKey } from 'src/Libs/flow/parsers'
import { saveToLocalStorage } from 'src/Libs/flow/hooks'
import { getTimeAsISO } from 'src/Libs/flow/installation/utils'
import { AddIcon } from 'src/Libs/flow/icons'
import { makeStyles } from 'tss-react/mui'
import { getWeekdayIndexes } from '../../utils'
import { ScheduleType } from '../../types'
import { ChargingWeeklyScheduleInput } from './ChargingWeeklyScheduleInput'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

const useStyles = makeStyles()(theme => ({
  container: {
    padding: theme.spacing(2, 4),
    border: `1px solid ${theme.palette.grey.grey06}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(1),
  },
  spaceBetweenRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  iconButton: {
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.primary.main,
    width: theme.spacing(6),
    height: theme.spacing(6),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}))

interface ChargingWeeklyScheduleProps {
  evseDeviceId: string
  evseType: keyof InstallationFormModel['chargingStations']
  index: number
  scheduleType: ScheduleType
}

/** Overview of the input for the weekday-schedule-settings, with a button to add new schedules */
export const ChargingWeeklySchedule: React.FC<ChargingWeeklyScheduleProps> = ({ evseDeviceId, evseType, index, scheduleType }) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { classes } = useStyles()

  const { control } = useInstallationForm()

  const { fields, append, remove } = useFieldArray({
    control,
    name: `chargingStations.${evseType}.${index}.${scheduleType}`,
  })

  const schedules = useWatch({
    control,
    name: `chargingStations.${evseType}.${index}.${scheduleType}`,
  })

  useEffect(() => {
    if (!evseDeviceId) return
    const key =
      scheduleType === 'safePvSchedule' ? getEvseSafePvScheduleLocalStorageKey(evseDeviceId) : getEvseEfficientPvScheduleLocalStorageKey(evseDeviceId)
    saveToLocalStorage(key, schedules)
  }, [schedules, evseDeviceId, scheduleType])

  const weekdayIndexes = getWeekdayIndexes(schedules)
  const hasAvailableWeekday = Object.values(weekdayIndexes).includes(null)

  const addSchedule = (): void =>
    append(
      {
        energyWanted: 1,
        departureTime: getTimeAsISO('06:00'),
        weekDays: {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false,
        },
      },
      { shouldFocus: false }
    )

  return (
    <Stack className={classes.container}>
      <Stack className={classes.spaceBetweenRow}>
        <Typography variant="h3">{t('installation.evseEditor.chargingTargetRule')}</Typography>
        <IconButton onClick={addSchedule} disabled={!hasAvailableWeekday || !canEdit} className={classes.iconButton}>
          <AddIcon />
        </IconButton>
      </Stack>
      <Stack gap={4}>
        {fields.map((item, calendarIndex) => (
          <ChargingWeeklyScheduleInput
            key={item.id}
            evseIndex={index}
            evseType={evseType}
            scheduleType={scheduleType}
            calendarIndex={calendarIndex}
            remove={() => remove(calendarIndex)}
            weekdayIndexes={weekdayIndexes}
            disableDeleteButton={fields.length <= 1}
          />
        ))}
      </Stack>

      {canEdit && !hasAvailableWeekday && (
        <Typography variant="caption">{t('user-settings.charging-priority-page.schedule.all-days-used')}</Typography>
      )}
    </Stack>
  )
}
